import React, { useState } from 'react';
import '../styles/CareersPage.css';

const CareersPage = () => {
  const [showApplicationForm, setShowApplicationForm] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState({ type: '', text: '' });
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    linkedIn: '',
    nationality: '',
    position: 'IT Technician Intern',
    experience: '',
    whyInterested: '',
    relevantProject: '',
    noticePeriod: '',
    workLocation: '',
    cv: null
  });
  const [errors, setErrors] = useState({});

  const openings = [
    {
      id: 1,
      title: 'IT Technician Intern',
      location: 'Windhoek, Namibia',
      type: 'Full-time',
      description: 'Join our team to help ensure the quality and reliability of our GRIDx smart meter technology through comprehensive testing and validation.',
      requirements: [
        'Strong understanding of software testing principles, methodologies, and best practices',
        'Experience with designing and executing test plans and test cases',
        'Ability to analyze data and validate software functionality',
        'Comfortable working with software tools and IT systems',
        'Strong problem-solving mindset and attention to detail',
        'Curiosity and eagerness to learn new technologies, tools, and processes',
        'Ability to collaborate effectively with cross-functional teams'
      ]
    }
  ];

  const nationalities = [
    'Namibian',
    'South African',
    'Botswanan',
    'Zimbabwean',
    'Zambian',
    'Other'
  ];

  const experienceLevels = [
    'Entry Level (0-1 years)',
    '1-2 years',
    '2-3 years',
    '3-5 years',
    '5+ years'
  ];

  const noticePeriods = [
    'Immediate',
    '1 week',
    '2 weeks',
    '1 month',
    '2 months',
    '3 months'
  ];

  const workLocations = [
    'Windhoek, Namibia',
    'Remote',
    'Hybrid'
  ];

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      if (!formData.fullName.trim()) newErrors.fullName = 'Full name is required';
      if (!formData.email.trim()) {
        newErrors.email = 'Email is required';
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = 'Email is invalid';
      }
      if (!formData.phone.trim()) {
        newErrors.phone = 'Phone number is required';
      } else if (!/^[\d\s+()-]+$/.test(formData.phone)) {
        newErrors.phone = 'Phone number is invalid';
      }
      if (!formData.nationality) newErrors.nationality = 'Nationality is required';
    }

    if (step === 2) {
      if (!formData.position) newErrors.position = 'Position is required';
      if (!formData.experience) newErrors.experience = 'Experience level is required';
    }

    if (step === 3) {
      if (!formData.whyInterested.trim()) {
        newErrors.whyInterested = 'This field is required';
      } else if (formData.whyInterested.trim().length < 50) {
        newErrors.whyInterested = 'Please provide at least 50 characters';
      }
      if (!formData.relevantProject.trim()) {
        newErrors.relevantProject = 'This field is required';
      } else if (formData.relevantProject.trim().length < 50) {
        newErrors.relevantProject = 'Please provide at least 50 characters';
      }
    }

    if (step === 4) {
      if (!formData.noticePeriod) newErrors.noticePeriod = 'Notice period is required';
      if (!formData.workLocation) newErrors.workLocation = 'Work location preference is required';
    }

    if (step === 5) {
      if (!formData.cv) {
        newErrors.cv = 'CV with all required documents is required';
      } else {
        const cvExt = formData.cv.name.split('.').pop().toLowerCase();
        if (!['pdf', 'docx'].includes(cvExt)) {
          newErrors.cv = 'CV must be in PDF or DOCX format';
        } else if (formData.cv.size > 10 * 1024 * 1024) {
          newErrors.cv = 'CV must not exceed 10 MB';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    setCurrentStep(currentStep - 1);
    setErrors({});
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };



  const handleCVChange = (e) => {
    const file = e.target.files[0];
    setFormData({ ...formData, cv: file });
    if (errors.cv) {
      setErrors({ ...errors, cv: '' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateStep(5)) {
      setIsSubmitting(true);
      setSubmitMessage({ type: '', text: '' });

      try {
        // Create FormData for multipart/form-data
        const formDataToSend = new FormData();
        
        // Add personal information
        formDataToSend.append('personalInformation[fullName]', formData.fullName);
        formDataToSend.append('personalInformation[email]', formData.email);
        formDataToSend.append('personalInformation[phone]', formData.phone);
        formDataToSend.append('personalInformation[linkedIn]', formData.linkedIn || null);
        formDataToSend.append('personalInformation[nationality]', formData.nationality);
        
        // Add job specific
        formDataToSend.append('jobSpecific[position]', formData.position);
        formDataToSend.append('jobSpecific[experience]', formData.experience);
        
        // Add motivation
        formDataToSend.append('motivation[whyInterested]', formData.whyInterested);
        formDataToSend.append('motivation[relevantProject]', formData.relevantProject);
        
        // Add availability
        formDataToSend.append('availability[noticePeriod]', formData.noticePeriod);
        formDataToSend.append('availability[workLocation]', formData.workLocation);
        
        // Add attachments metadata
        formDataToSend.append('attachments[cv]', formData.cv.name);
        formDataToSend.append('attachments[cvSize]', `${(formData.cv.size / 1024 / 1024).toFixed(2)} MB`);
        
        // Add CV file
        formDataToSend.append('cv', formData.cv);

        const response = await fetch(`${process.env.REACT_APP_API_URL}/recruitment/apply`, {
          method: 'POST',
          body: formDataToSend
        });

        const result = await response.json();

        if (result.statusCode === 200) {
          setSubmitMessage({ type: 'success', text: 'Application submitted successfully! We\'ll review your application and get back to you soon.' });
          
          // Reset form after successful submission
          setTimeout(() => {
            setShowApplicationForm(false);
            setCurrentStep(1);
            setFormData({
              fullName: '',
              email: '',
              phone: '',
              linkedIn: '',
              nationality: '',
              position: 'IT Technician Intern',
              experience: '',
              whyInterested: '',
              relevantProject: '',
              noticePeriod: '',
              workLocation: '',
              cv: null
            });
            setErrors({});
            setSubmitMessage({ type: '', text: '' });
          }, 3000);
        } else {
          setSubmitMessage({ type: 'error', text: result.message || 'Failed to submit application. Please try again.' });
        }
      } catch (error) {
        setSubmitMessage({ type: 'error', text: 'Network error. Please check your connection and try again.' });
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const values = [
    {
      id: 1,
      icon: 'lightbulb',
      title: 'Innovation',
      description: 'We encourage creative thinking and embrace new ideas that push boundaries.'
    },
    {
      id: 2,
      icon: 'users',
      title: 'Collaboration',
      description: 'We believe in the power of diverse teams working together toward common goals.'
    },
    {
      id: 3,
      icon: 'globe',
      title: 'Impact',
      description: 'Our work directly improves lives by expanding access to energy and connectivity.'
    },
    {
      id: 4,
      icon: 'trending-up',
      title: 'Growth',
      description: 'We invest in our team through continuous learning and professional development.'
    }
  ];

  return (
    <div className="careers-page">
      <div className="careers-page__hero">
        <div className="careers-page__hero-content">
          <h1 className="careers-page__title">Join Our Mission</h1>
          <p className="careers-page__subtitle">
            Help us bridge the digital divide and transform communities across Africa through innovative technology solutions.
          </p>
        </div>
      </div>

      <div className="careers-page__container">
        <div className="careers-page__section">
          <h2 className="careers-page__section-title">Why Work at Pulsar?</h2>
          <div className="careers-page__values-grid">
            {values.map((value) => (
              <div key={value.id} className="careers-page__value-card">
                <div className="careers-page__value-icon">
                  {value.icon === 'lightbulb' && (
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M9 18h6M10 22h4M15 8a5 5 0 1 0-6 4.9V17h6v-4.1A5 5 0 0 0 15 8z"/>
                    </svg>
                  )}
                  {value.icon === 'users' && (
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75M13 7a4 4 0 1 1-8 0 4 4 0 0 1 8 0z"/>
                    </svg>
                  )}
                  {value.icon === 'globe' && (
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/>
                      <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                    </svg>
                  )}
                  {value.icon === 'trending-up' && (
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/>
                      <polyline points="17 6 23 6 23 12"/>
                    </svg>
                  )}
                </div>
                <h3 className="careers-page__value-title">{value.title}</h3>
                <p className="careers-page__value-description">{value.description}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="careers-page__section">
          <h2 className="careers-page__section-title">Open Positions</h2>
          <div className="careers-page__jobs">
            {openings.map((job) => (
              <div key={job.id} className="careers-page__job-card">
                <div className="careers-page__job-header">
                  <div>
                    <h3 className="careers-page__job-title">{job.title}</h3>
                    <div className="careers-page__job-meta">
                      <span className="careers-page__job-location">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
                          <circle cx="12" cy="10" r="3"/>
                        </svg>
                        {job.location}
                      </span>
                      <span className="careers-page__job-type">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10"/>
                          <polyline points="12 6 12 12 16 14"/>
                        </svg>
                        {job.type}
                      </span>
                    </div>
                  </div>
                </div>
                <p className="careers-page__job-description">{job.description}</p>
                {job.requirements && (
                  <div className="careers-page__job-requirements">
                    <h4>Basic Requirements:</h4>
                    <ul>
                      {job.requirements.map((req, index) => (
                        <li key={index}>{req}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <button 
                  onClick={() => setShowApplicationForm(true)}
                  className="careers-page__apply-button"
                >
                  Apply Now
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Application Form Modal */}
        {showApplicationForm && (
          <div className="application-modal">
            <div className="application-modal__overlay" onClick={() => setShowApplicationForm(false)}></div>
            <div className="application-modal__content">
              <button 
                className="application-modal__close"
                onClick={() => setShowApplicationForm(false)}
              >
                ×
              </button>
              
              <div className="application-form">
                <div className="application-form__header">
                  <h2 className="application-form__title">Application Form</h2>
                  <div className="application-form__steps">
                    {[1, 2, 3, 4, 5].map((step) => (
                      <div 
                        key={step}
                        className={`application-form__step ${currentStep >= step ? 'active' : ''} ${currentStep === step ? 'current' : ''}`}
                      >
                        {step}
                      </div>
                    ))}
                  </div>
                </div>

                {submitMessage.text && (
                  <div className={`application-form__message application-form__message--${submitMessage.type}`}>
                    {submitMessage.type === 'success' ? (
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                        <polyline points="22 4 12 14.01 9 11.01"/>
                      </svg>
                    ) : (
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10"/>
                        <line x1="12" y1="8" x2="12" y2="12"/>
                        <line x1="12" y1="16" x2="12.01" y2="16"/>
                      </svg>
                    )}
                    <span>{submitMessage.text}</span>
                  </div>
                )}

                <form onSubmit={handleSubmit}>
                  {/* Step 1: Personal Information */}
                  {currentStep === 1 && (
                    <div className="application-form__step-content">
                      <h3 className="application-form__step-title">Personal Information</h3>
                      
                      <div className="application-form__group">
                        <label>Full Name *</label>
                        <input
                          type="text"
                          name="fullName"
                          value={formData.fullName}
                          onChange={handleInputChange}
                          placeholder="John Doe"
                        />
                        {errors.fullName && <span className="application-form__error">{errors.fullName}</span>}
                      </div>

                      <div className="application-form__group">
                        <label>Email Address *</label>
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="john.doe@example.com"
                        />
                        {errors.email && <span className="application-form__error">{errors.email}</span>}
                      </div>

                      <div className="application-form__group">
                        <label>Phone Number *</label>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          placeholder="+264 81 234 5678"
                        />
                        {errors.phone && <span className="application-form__error">{errors.phone}</span>}
                      </div>

                      <div className="application-form__group">
                        <label>LinkedIn / Portfolio URL (Optional)</label>
                        <input
                          type="url"
                          name="linkedIn"
                          value={formData.linkedIn}
                          onChange={handleInputChange}
                          placeholder="https://linkedin.com/in/johndoe"
                        />
                      </div>

                      <div className="application-form__group">
                        <label>Nationality *</label>
                        <select
                          name="nationality"
                          value={formData.nationality}
                          onChange={handleInputChange}
                        >
                          <option value="">Select your nationality</option>
                          {nationalities.map((nat) => (
                            <option key={nat} value={nat}>{nat}</option>
                          ))}
                        </select>
                        {errors.nationality && <span className="application-form__error">{errors.nationality}</span>}
                      </div>
                    </div>
                  )}

                  {/* Step 2: Job-Specific Questions */}
                  {currentStep === 2 && (
                    <div className="application-form__step-content">
                      <h3 className="application-form__step-title">Job-Specific Questions</h3>
                      
                      <div className="application-form__group">
                        <label>Position *</label>
                        <select
                          name="position"
                          value={formData.position}
                          onChange={handleInputChange}
                        >
                          <option value="IT Technician Intern">IT Technician Intern</option>
                        </select>
                        {errors.position && <span className="application-form__error">{errors.position}</span>}
                      </div>

                      <div className="application-form__group">
                        <label>Experience Level *</label>
                        <select
                          name="experience"
                          value={formData.experience}
                          onChange={handleInputChange}
                        >
                          <option value="">Select your experience level</option>
                          {experienceLevels.map((level) => (
                            <option key={level} value={level}>{level}</option>
                          ))}
                        </select>
                        {errors.experience && <span className="application-form__error">{errors.experience}</span>}
                      </div>
                    </div>
                  )}

                  {/* Step 3: Motivation / Fit */}
                  {currentStep === 3 && (
                    <div className="application-form__step-content">
                      <h3 className="application-form__step-title">Motivation & Fit</h3>
                      
                      <div className="application-form__group">
                        <label>Why are you interested in this position? * (Min 50 characters)</label>
                        <textarea
                          name="whyInterested"
                          value={formData.whyInterested}
                          onChange={handleInputChange}
                          placeholder="Share your motivation for applying to this role..."
                          rows="5"
                        />
                        <span className="application-form__char-count">
                          {formData.whyInterested.length} characters
                        </span>
                        {errors.whyInterested && <span className="application-form__error">{errors.whyInterested}</span>}
                      </div>

                      <div className="application-form__group">
                        <label>Describe a relevant project or experience * (Min 50 characters)</label>
                        <textarea
                          name="relevantProject"
                          value={formData.relevantProject}
                          onChange={handleInputChange}
                          placeholder="Tell us about a project or experience that's relevant to this role..."
                          rows="5"
                        />
                        <span className="application-form__char-count">
                          {formData.relevantProject.length} characters
                        </span>
                        {errors.relevantProject && <span className="application-form__error">{errors.relevantProject}</span>}
                      </div>
                    </div>
                  )}

                  {/* Step 4: Availability */}
                  {currentStep === 4 && (
                    <div className="application-form__step-content">
                      <h3 className="application-form__step-title">Availability</h3>
                      
                      <div className="application-form__group">
                        <label>Notice Period / Availability to Start *</label>
                        <select
                          name="noticePeriod"
                          value={formData.noticePeriod}
                          onChange={handleInputChange}
                        >
                          <option value="">Select your notice period</option>
                          {noticePeriods.map((period) => (
                            <option key={period} value={period}>{period}</option>
                          ))}
                        </select>
                        {errors.noticePeriod && <span className="application-form__error">{errors.noticePeriod}</span>}
                      </div>

                      <div className="application-form__group">
                        <label>Preferred Work Location *</label>
                        <select
                          name="workLocation"
                          value={formData.workLocation}
                          onChange={handleInputChange}
                        >
                          <option value="">Select your preferred location</option>
                          {workLocations.map((location) => (
                            <option key={location} value={location}>{location}</option>
                          ))}
                        </select>
                        {errors.workLocation && <span className="application-form__error">{errors.workLocation}</span>}
                      </div>
                    </div>
                  )}

                  {/* Step 5: Attachments */}
                  {currentStep === 5 && (
                    <div className="application-form__step-content">
                      <h3 className="application-form__step-title">Attachments</h3>
                      
                      <div className="application-form__group">
                        <label>CV with All Required Documents * (PDF or DOCX, max 10 MB)</label>
                        <p style={{ fontSize: '0.875rem', color: '#6e6e73', marginTop: '0.5rem', marginBottom: '1rem' }}>
                          Please upload a single document containing your cv and all other required documents.
                        </p>
                        <input
                          type="file"
                          accept=".pdf,.docx"
                          onChange={handleCVChange}
                        />
                        {formData.cv && (
                          <span className="application-form__file-name">
                            Selected: {formData.cv.name} ({(formData.cv.size / 1024 / 1024).toFixed(2)} MB)
                          </span>
                        )}
                        {errors.cv && <span className="application-form__error">{errors.cv}</span>}
                      </div>
                    </div>
                  )}

                  {/* Navigation Buttons */}
                  <div className="application-form__buttons">
                    {currentStep > 1 && (
                      <button 
                        type="button"
                        onClick={handleBack}
                        className="application-form__button application-form__button--back"
                        disabled={isSubmitting}
                      >
                        Back
                      </button>
                    )}
                    
                    {currentStep < 5 ? (
                      <button 
                        type="button"
                        onClick={handleNext}
                        className="application-form__button application-form__button--next"
                      >
                        Next
                      </button>
                    ) : (
                      <button 
                        type="submit"
                        className="application-form__button application-form__button--submit"
                        disabled={isSubmitting}
                      >
                        {isSubmitting ? (
                          <>
                            <svg className="spinner" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <circle cx="12" cy="12" r="10" strokeOpacity="0.25"/>
                              <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round"/>
                            </svg>
                            Submitting...
                          </>
                        ) : 'Submit Application'}
                      </button>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* <div className="careers-page__cta">
          <div className="careers-page__cta-content">
            <h2 className="careers-page__cta-title">Don't See the Right Role?</h2>
            <p className="careers-page__cta-text">
              We're always looking for talented individuals who are passionate about using technology to make a difference. 
              Send us your CV and let us know how you'd like to contribute.
            </p>
            <a href="mailto:careers@pulsarelectronic.com" className="careers-page__cta-button">
              Send Your CV
            </a>
          </div>
        </div> */}
      </div>
    </div>
  );
};

export default CareersPage;
